<script setup lang="ts">
    // @ts-nocheck (pga Vuex 3 + Vue 2.7 ger svårlösta typfel)
    import { ref, computed, onMounted } from "vue";
    import { useStore } from "@/store";
    import { getConfig, klinikenApi } from "@/api";
    import tabComponents from "@/tabs";

    interface ActionSubject {
        action: string;
        subject: string;
    }

    const emit = defineEmits<{ (e: "toggleSize", minimized: boolean): void }>();

    const store = useStore();
    const minimized = ref(false);

    const menu = computed(() => store.getters["tabs/menu"]);
    const can = computed(() => store.getters["ability/can"]);

    const ACTION_SUBJECT_MAP: Record<string, ActionSubject> = {
        Operationsanmalan: { action: "create", subject: "anmalan" },
        Operationskoordinering: { action: "read", subject: "operationskoordinering" },
        Preanestesibedomning: { action: "create", subject: "preanestesibedomning" },
        Operationskalender: { action: "read", subject: "operationskalender" },
        Operationsoversikt: { action: "read", subject: "operationsoversikt" },
        Sparratid: { action: "read", subject: "sparradtid" },
        PagaendeOperation: { action: "read", subject: "pagaendeoperation" },
        Vardplatsbestallning: { action: "create", subject: "vardplatsbestallning" },
        Vardplatsbestallningsoversikt: { action: "read", subject: "vardplatsbestallning" },
        Vardplatsoversikt: { action: "read", subject: "vardplatsoversikt" },
        Vardplatskalender: { action: "read", subject: "vardplatskalender" },
        Vardplatsfakturering: { action: "read", subject: "vardplatsfakturering" },
        Typoperationer: { action: "update", subject: "typoperation" },
    };

    const isLankDisabled = (lank: string): boolean => {
        const actionSubject = ACTION_SUBJECT_MAP[lank.komponent];
        if (actionSubject) {
            // Om länken kräver superuser eller verksamhetsadmin och användaren är det så ska hen alltid ha tillgång
            // till den, även om hen inte har den specifika rättigheten som krävs för länken. Detta är för att
            // underlätta åtkomst till viktiga funktioner för administratörer.
            if (
                (lank.require_superuser && store.getters["userData/user"].is_superuser) ||
                (lank.require_verksamhetsadmin && store.getters["userData/user"].is_verksamhetsadmin)
            ) {
                return false;
            } else {
                return !can.value(actionSubject);
            }
        } else {
            return false;
        }
    };

    const toggleSize = (): void => {
        minimized.value = !minimized.value;
        emit("toggleSize", minimized.value);
    };

    const getComponent = (name: string | undefined) => {
        if (name !== undefined) {
            return tabComponents[name];
        }
    };

    const getMenuIcon = (name: string): string | undefined => {
        return getComponent(name)?.icon;
    };

    const openLank = (name: string): void => {
        const component = getComponent(name);
        if (component !== undefined) {
            store.dispatch("tabs/openTab", { component });
        }
    };

    onMounted(async () => {
        const response = await klinikenApi.get("/core/meny/", getConfig(), 1);
        store.commit("tabs/setMenu", response.data);
    });
</script>

<template>
    <div id="kliniken-menu" :class="{ minimized: minimized }">
        <h2 aria-hidden="false">Huvudmeny</h2>
        <div class="menu-container">
            <div class="size-toggle">
                <button role="button" @click="toggleSize">
                    <img
                        v-tooltip="{
                            content: 'Minimera vänstermeny',
                            theme: 'kliniken-tooltip',
                            disabled: minimized,
                        }"
                        class="menu-button"
                        height="35px"
                        src="@/assets/menu.svg"
                        width="35px"
                    />
                </button>
            </div>
            <div class="menu">
                <ul v-for="rubrik in menu" :key="rubrik.namn" class="groups">
                    <li>
                        <span v-if="!minimized" class="title">{{ rubrik.namn }}</span>
                        <ul class="items">
                            <li
                                v-for="lank in rubrik.lankar"
                                :key="lank.namn"
                                :class="{
                                    open: lank.open,
                                    visible: lank.visible,
                                    disabled: isLankDisabled(lank),
                                }"
                            >
                                <a href="#" @click.prevent="openLank(lank.komponent)">
                                    <img
                                        v-tooltip="{
                                            content: lank.namn,
                                            theme: 'kliniken-tooltip',
                                            disabled: !minimized,
                                        }"
                                        :disabled="{ disabledLinks: isLankDisabled(lank) }"
                                        :src="getMenuIcon(lank.komponent)"
                                        alt=""
                                        height="22px"
                                        width="22px"
                                    />
                                    <span v-if="!minimized">{{ lank.namn }}</span>
                                </a>
                            </li>
                        </ul>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import "@/style/variables";

    #kliniken-menu {
        background-color: $main-grey-bg;

        .size-toggle {
            min-height: 64px;
            border-bottom: 1px solid #dbe3e3;
            display: flex;
            justify-content: flex-end;

            button {
                border: none;
                outline: none;
                background-color: inherit;
            }
        }

        .menu-container {
            box-shadow: 2px 0 7px $color-box-shadow;
        }

        .menu {
            height: calc(100vh - 64px - $header-height);
            overflow-x: hidden;
            overflow-y: auto;

            .groups {
                list-style: none;
                padding: 0;
                margin: 0;

                .title {
                    padding-left: 25px;
                    font-family: Roboto, sans-serif;
                    color: #6c8188;
                    font-size: 19px;
                    line-height: 54px;
                }

                .items {
                    list-style: none;
                    padding: 0;
                    margin: 0;

                    li:first-of-type {
                        border-top: 1px solid #dbe3e3;
                    }

                    li:last-of-type {
                        border-bottom: 1px solid #dbe3e3;
                    }
                }

                li {
                    font-family: Roboto, sans-serif;
                    font-size: 16px;
                    font-weight: 700;
                    line-height: 46px;
                    white-space: nowrap;
                    min-height: 46px;
                    height: fit-content;

                    a {
                        padding-left: 30px;
                        display: block;
                        color: #6c8188;
                        text-decoration: none;

                        img {
                            width: 18px;
                            height: 22px;
                        }

                        span {
                            padding-left: 30px;
                        }
                    }

                    &.open {
                        background: linear-gradient(to right, #ca346f 0px, #ca346f 5px);
                        background-size: 5px 100%;
                        background-repeat: no-repeat;
                        background-position: left;

                        a {
                            color: #ca346f;
                        }
                    }
                }

                .disabled {
                    pointer-events: none;
                    opacity: $disabled-opacity;
                }
            }
        }

        &.minimized {
            .logo {
                img {
                    height: 43px;
                    width: 41px;
                    margin-right: 17px;
                    margin-left: 17px;
                }
            }

            .size-toggle {
                justify-content: center;

                button {
                    transform: rotate(180deg);
                }
            }

            .menu {
                padding: 0;
                justify-content: center;

                .groups {
                    .items {
                        li {
                            display: grid;

                            a {
                                display: grid;
                                place-items: center;
                                width: 100%;
                                padding-left: 0;
                            }
                        }
                    }
                }
            }
        }
    }
</style>
